import React, { useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { cn } from '../UI/cn'
import {
  getLatestActionSummary,
  ActionSummaryData,
} from '../../services/recommendations_api'
import { SignalSection } from './SignalSection'
import { CompanySection } from './CompanySection'
import { SummaryStatsHeader } from './SummaryStatsHeader'
import { SIGNAL_SECTIONS } from './constants'
import { Tabs } from '../FbUI/Tabs'
import { useRecommendationsPolling } from './useRecommendationsPolling'
import { useActionClickManager } from './useActionClickManager'
import { useExpansionState } from './useExpansionState'
import {
  groupActionsByCompany,
  groupActionsBySignal,
  partitionActionsByStatus,
} from './recommendationVisibility'

type ViewMode = 'signal' | 'company'

interface RecommendationsCardProps {
  className?: string
  /** Optional user ID to view another user's summary (superusers only) */
  userId?: number
  /** Callback fired whenever the summary changes */
  onSummaryLoaded?: (summary: ActionSummaryData | null) => void
}

/**
 * Displays AI-generated daily recommendations for the current user.
 *
 * Shows prioritized action items grouped by either:
 * - Recommendation type: unreplied_email, inactive_deal
 * - Company: grouped by contact company
 *
 * Superusers can pass a userId prop to view another user's summary.
 */
export const RecommendationsCard: React.FC<RecommendationsCardProps> = ({
  className,
  userId,
  onSummaryLoaded,
}) => {
  const [viewMode, setViewMode] = useState<ViewMode>('signal')
  const [showResolved, setShowResolved] = useState(false)
  const viewTabs = useMemo(
    () => [
      { key: 'signal' as ViewMode, title: 'Recommendation Type' },
      { key: 'company' as ViewMode, title: 'Company' },
    ],
    []
  )

  // Expansion state management
  const activeExpansion = useExpansionState()
  const resolvedExpansion = useExpansionState()

  const summaryQuery = useQuery<ActionSummaryData | null, Error>({
    queryKey: ['recommendations', userId ?? 'self'],
    queryFn: async () => {
      try {
        return await getLatestActionSummary(userId)
      } catch (err) {
        const axiosError = err as AxiosError<{ detail?: string }>
        if (axiosError.response?.status === 404) {
          return null
        }
        throw new Error(
          axiosError.response?.data?.detail ||
            axiosError.message ||
            'Failed to fetch recommendations'
        )
      }
    },
    refetchOnWindowFocus: false,
  })

  const summary = summaryQuery.data

  // Polling for GENERATING status
  useRecommendationsPolling({
    summary,
    refetch: summaryQuery.refetch,
    userId,
  })

  useEffect(() => {
    onSummaryLoaded?.(summary ?? null)
  }, [summary, onSummaryLoaded])

  useEffect(() => {
    setShowResolved(false)
  }, [summary?.id, userId])

  // Action click management
  const {
    handleActionClick,
    handleSentimentClick,
    handleStatusClick,
    getSentimentForAction,
    getStatusForAction,
  } = useActionClickManager({ summary })

  const allActions = summary?.summary_data?.enriched_actions ?? []
  const { activeActions, resolvedActions } = useMemo(
    () => partitionActionsByStatus(allActions, getStatusForAction),
    [allActions, getStatusForAction]
  )

  const actionsBySignal = useMemo(
    () => groupActionsBySignal(activeActions),
    [activeActions]
  )
  const resolvedActionsBySignal = useMemo(
    () => groupActionsBySignal(resolvedActions),
    [resolvedActions]
  )
  const actionsByCompany = useMemo(
    () => groupActionsByCompany(activeActions),
    [activeActions]
  )
  const resolvedActionsByCompany = useMemo(
    () => groupActionsByCompany(resolvedActions),
    [resolvedActions]
  )

  if (summaryQuery.isLoading) {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface p-6 shadow-sm',
          className
        )}
      >
        <div className="animate-pulse space-y-4">
          <div className="h-6 w-1/3 rounded bg-surface-disabled"></div>
          <div className="space-y-2">
            <div className="h-4 w-full rounded bg-surface-disabled"></div>
            <div className="h-4 w-5/6 rounded bg-surface-disabled"></div>
            <div className="h-4 w-4/6 rounded bg-surface-disabled"></div>
          </div>
        </div>
      </div>
    )
  }

  const queryError = summaryQuery.error as Error | undefined

  if (queryError) {
    return (
      <div
        className={cn(
          'rounded-lg border border-error-border bg-error-bg p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-error">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span className="font-medium">Error loading recommendations</span>
        </div>
        <p className="mt-2 text-sm text-error">{queryError.message}</p>
      </div>
    )
  }

  // No summary exists yet (status is null)
  if (!summary || summary.status === null) {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface p-6 shadow-sm',
          className
        )}
      >
        <div className="text-center">
          <svg
            className="mx-auto h-12 w-12 text-fg-muted"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
            />
          </svg>
          <h3 className="mt-2 text-sm font-medium text-fg">
            No recommendations available
          </h3>
          <p className="mt-1 text-sm text-fg-secondary">
            Click &quot;Generate Recommendations&quot; to create your daily
            recommendations
          </p>
        </div>
      </div>
    )
  }

  if (summary.status === 'FAILED') {
    return (
      <div
        className={cn(
          'rounded-lg border border-warning-border bg-warning-bg p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-warning">
          <svg
            className="h-5 w-5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
            />
          </svg>
          <span className="font-medium">Summary generation failed</span>
        </div>
        {summary.error_message && (
          <p className="mt-2 text-sm text-warning">{summary.error_message}</p>
        )}
      </div>
    )
  }

  if (summary.status === 'GENERATING') {
    return (
      <div
        className={cn(
          'rounded-lg border border-line bg-surface p-6',
          className
        )}
      >
        <div className="flex items-center gap-2 text-fg-tertiary">
          <svg className="h-5 w-5 animate-spin" fill="none" viewBox="0 0 24 24">
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <span className="font-medium">
            Generating your recommendations...
          </span>
        </div>
        <p className="mt-2 text-sm text-fg-secondary">
          This usually takes 10-30 seconds
        </p>
      </div>
    )
  }

  const hasActions = allActions.length > 0
  const hasActiveActions = activeActions.length > 0
  const hasResolvedActions = resolvedActions.length > 0
  const hasSignalData =
    Object.keys(actionsBySignal).length > 0 ||
    (showResolved && Object.keys(resolvedActionsBySignal).length > 0)

  return (
    <div
      className={cn(
        'rounded-lg border border-line bg-surface shadow-sm overflow-hidden',
        className
      )}
    >
      {hasActions ? (
        <div className="bg-surface-alt px-4 py-4 space-y-4">
          {summary.summary_data && (
            <SummaryStatsHeader
              data={summary.summary_data}
              visibleActions={activeActions}
              hiddenResolvedCount={resolvedActions.length}
            />
          )}

          {(hasSignalData || hasResolvedActions) && (
            <div className="flex items-center justify-between">
              <div className="flex items-center flex-1 min-w-0">
                <span className="text-xs text-fg-secondary">Group by:</span>
                <div className="flex-1 ml-3">
                  <Tabs<ViewMode>
                    tabs={viewTabs}
                    selectedTab={viewMode}
                    setSelectedTab={setViewMode}
                    isSmall
                    noGap
                  />
                </div>
              </div>
              {hasResolvedActions && (
                <button
                  type="button"
                  onClick={() => setShowResolved((prev) => !prev)}
                  className="ml-3 inline-flex items-center rounded-full border border-line-subtle bg-surface px-3 py-1 text-xs font-medium text-fg-secondary transition-colors hover:border-line hover:text-fg"
                >
                  {showResolved
                    ? 'Hide resolved'
                    : `Show resolved (${resolvedActions.length})`}
                </button>
              )}
            </div>
          )}

          {viewMode === 'company' ? (
            <div className="space-y-4">
              {hasActiveActions ? (
                <div className="space-y-2">
                  {actionsByCompany.map((company) => (
                    <CompanySection
                      key={company.companyId}
                      companyName={company.companyName}
                      companyId={company.companyId}
                      actions={company.actions}
                      isExpanded={
                        activeExpansion.expandedCompanies[company.companyId] ??
                        false
                      }
                      onToggle={() =>
                        activeExpansion.toggleCompany(company.companyId)
                      }
                      onActionClick={handleActionClick}
                      onSentimentClick={handleSentimentClick}
                      getSentimentForAction={getSentimentForAction}
                      onStatusClick={handleStatusClick}
                      getStatusForAction={getStatusForAction}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-5 text-center">
                  <p className="text-sm font-medium text-fg">
                    No active recommendations
                  </p>
                  <p className="mt-1 text-sm text-fg-secondary">
                    {hasResolvedActions
                      ? 'Resolved items are hidden from your active queue.'
                      : 'Your pipeline looks healthy.'}
                  </p>
                </div>
              )}

              {showResolved && hasResolvedActions && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-fg">Resolved</h3>
                    <span className="text-xs text-fg-muted">
                      Done and dismissed items
                    </span>
                  </div>
                  {resolvedActionsByCompany.map((company) => (
                    <CompanySection
                      key={`resolved-${company.companyId}`}
                      companyName={company.companyName}
                      companyId={company.companyId}
                      actions={company.actions}
                      isExpanded={
                        resolvedExpansion.expandedCompanies[
                          company.companyId
                        ] ?? false
                      }
                      onToggle={() =>
                        resolvedExpansion.toggleCompany(company.companyId)
                      }
                      onActionClick={handleActionClick}
                      onSentimentClick={handleSentimentClick}
                      getSentimentForAction={getSentimentForAction}
                      onStatusClick={handleStatusClick}
                      getStatusForAction={getStatusForAction}
                    />
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {hasActiveActions ? (
                <div className="space-y-2">
                  {SIGNAL_SECTIONS.map((section) => (
                    <SignalSection
                      key={section.key}
                      title={section.title}
                      description={section.description}
                      Icon={section.Icon}
                      iconColor={section.iconColor}
                      isExpanded={
                        activeExpansion.signalSectionExpanded[section.key] ??
                        true
                      }
                      onToggleExpanded={() =>
                        activeExpansion.toggleSignalSection(section.key)
                      }
                      actions={actionsBySignal[section.key] ?? []}
                      showAll={activeExpansion.expandedSections[section.key]}
                      onToggleShowAll={() =>
                        activeExpansion.toggleSection(section.key)
                      }
                      signalType={section.key}
                      onActionClick={handleActionClick}
                      onSentimentClick={handleSentimentClick}
                      getSentimentForAction={getSentimentForAction}
                      onStatusClick={handleStatusClick}
                      getStatusForAction={getStatusForAction}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-lg border border-dashed border-line bg-surface px-4 py-5 text-center">
                  <p className="text-sm font-medium text-fg">
                    No active recommendations
                  </p>
                  <p className="mt-1 text-sm text-fg-secondary">
                    {hasResolvedActions
                      ? 'Resolved items are hidden from your active queue.'
                      : 'Your pipeline looks healthy.'}
                  </p>
                </div>
              )}

              {showResolved && hasResolvedActions && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-semibold text-fg">Resolved</h3>
                    <span className="text-xs text-fg-muted">
                      Done and dismissed items
                    </span>
                  </div>
                  {SIGNAL_SECTIONS.map((section) => (
                    <SignalSection
                      key={`resolved-${section.key}`}
                      title={section.title}
                      description={section.description}
                      Icon={section.Icon}
                      iconColor={section.iconColor}
                      isExpanded={
                        resolvedExpansion.signalSectionExpanded[section.key] ??
                        true
                      }
                      onToggleExpanded={() =>
                        resolvedExpansion.toggleSignalSection(section.key)
                      }
                      actions={resolvedActionsBySignal[section.key] ?? []}
                      showAll={
                        resolvedExpansion.expandedSections[section.key] ?? false
                      }
                      onToggleShowAll={() =>
                        resolvedExpansion.toggleSection(section.key)
                      }
                      signalType={section.key}
                      onActionClick={handleActionClick}
                      onSentimentClick={handleSentimentClick}
                      getSentimentForAction={getSentimentForAction}
                      onStatusClick={handleStatusClick}
                      getStatusForAction={getStatusForAction}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="p-6 text-center">
          <p className="text-fg-secondary">
            No actions found. Your pipeline looks healthy!
          </p>
        </div>
      )}

      {/* Generation duration footer */}
      {summary.created && (
        <div className="px-4 py-2 border-t border-line-subtle flex items-center justify-between">
          {summary.summary_data?.snoozed_count != null &&
            summary.summary_data.snoozed_count > 0 && (
              <span className="text-xs text-fg-muted">
                {summary.summary_data.snoozed_count} snoozed signal
                {summary.summary_data.snoozed_count !== 1 ? 's' : ''}
              </span>
            )}
          <span className="text-xs text-fg-muted ml-auto">
            Updated{' '}
            {new Date(summary.created).toLocaleDateString([], {
              month: 'short',
              day: 'numeric',
            })}{' '}
            {new Date(summary.created).toLocaleTimeString([], {
              hour: 'numeric',
              minute: '2-digit',
            })}
          </span>
        </div>
      )}
    </div>
  )
}
